import {
    announceVideoPlayback,
    observeEmbeddedVideo,
} from "@iunctim/component-picture/Resources/Public/Scripts/utilities/VideoPlayback.js";

type VideoPlatform = "youtube" | "vimeo";

// add video behaviour configuration
// - play on video-platform
// - include iframe
// - include preview image that lazy loads iframe

// - puase video in sliders when slided
// - when lazy loaded, load all videos with same url on page at same time
// - lightbox: continue playing video in lightbox when opened
// @TODO

export class ExistingVideoHandler {
    private static instance: ExistingVideoHandler;
    private aspectRatio = 16 / 9;

    public static use(): ExistingVideoHandler {
        if (!ExistingVideoHandler.instance) {
            ExistingVideoHandler.instance = new ExistingVideoHandler();
        }
        return ExistingVideoHandler.instance;
    }

    constructor() {
        this.processVideos();
    }

    private processVideos() {
        const iframes = Array.from(
            document.querySelectorAll<HTMLIFrameElement>(
                [
                    `iframe[src*="youtube.com"]`,
                    `iframe[src*="youtube-nocookie.com"]`,
                    `iframe[src*="vimeo.com"]`,
                ].join(", "),
            ),
        );

        iframes.forEach((iframe) => {
            if (iframe.closest("[data-video-preview]")) return;

            const src = iframe.src;
            const platform = src.includes("youtube") ? "youtube" : "vimeo";
            const videoId = this.extractVideoId(src, platform);
            if (!videoId) return;

            // Replace iframe with preview wrapper
            const wrapper = this.createPreviewWrapper(
                videoId,
                platform,
                iframe,
            );
            iframe.replaceWith(wrapper);
        });
    }

    private extractVideoId(
        url: string,
        platform: VideoPlatform,
    ): string | null {
        if (platform === "youtube") {
            const match = url.match(/embed\/([^?&]+)/);
            return match ? match[1] : null;
        } else if (platform === "vimeo") {
            const match = url.match(/video\/(\d+)/);
            return match ? match[1] : null;
        }
        return null;
    }

    private getPreviewImage(videoId: string, platform: VideoPlatform): string {
        if (platform === "youtube") {
            return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
        } else {
            return `https://vumbnail.com/${videoId}.jpg`;
        }
    }

    private createPreviewWrapper(
        videoId: string,
        platform: VideoPlatform,
        originalIframe: HTMLIFrameElement,
    ): HTMLElement {
        const wrapper = document.createElement("div");
        wrapper.dataset.videoPreview = platform;
        wrapper.style.position = "relative";
        wrapper.style.width = "100%";
        wrapper.style.maxWidth = originalIframe.style.maxWidth || "100%";
        wrapper.style.aspectRatio = `${this.aspectRatio}`;
        wrapper.style.cursor = "pointer";
        wrapper.style.overflow = "hidden";
        wrapper.style.background = "#000";

        const img = document.createElement("img");
        img.src = this.getPreviewImage(videoId, platform);
        img.style.position = "absolute";
        img.style.top = "0";
        img.style.left = "0";
        img.style.width = "100%";
        img.style.height = "100%";
        img.style.objectFit = "cover";

        const playButton = document.createElement("div");
        playButton.style.position = "absolute";
        playButton.style.top = "50%";
        playButton.style.left = "50%";
        playButton.style.transform = "translate(-50%, -50%)";
        playButton.style.width = "64px";
        playButton.style.height = "64px";
        playButton.style.borderRadius = "50%";
        playButton.style.background = "#fff";
        playButton.style.display = "flex";
        playButton.style.justifyContent = "center";
        playButton.style.alignItems = "center";
        playButton.innerHTML =
            '<svg viewBox="0 0 64 64" width="56" height="56"><polygon points="25,18 25,46 46,32" fill="#000"/></svg>';

        wrapper.appendChild(img);
        wrapper.appendChild(playButton);

        wrapper.addEventListener(
            "click",
            () => {
                announceVideoPlayback(wrapper);

                const iframe = document.createElement("iframe");
                iframe.src = this.getAutoplayUrl(videoId, platform);
                iframe.allow = "autoplay; fullscreen; picture-in-picture";
                iframe.setAttribute("frameborder", "0");
                iframe.style.position = "absolute";
                iframe.style.top = "0";
                iframe.style.left = "0";
                iframe.style.width = "100%";
                iframe.style.height = "100%";

                observeEmbeddedVideo(iframe, platform);

                wrapper.innerHTML = "";
                wrapper.appendChild(iframe);
            },
            { once: true },
        );

        return wrapper;
    }

    private getAutoplayUrl(videoId: string, platform: VideoPlatform) {
        if (platform === "youtube") {
            const origin = encodeURIComponent(window.location.origin);
            return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&enablejsapi=1&playsinline=1&origin=${origin}`;
        } else {
            return `https://player.vimeo.com/video/${videoId}?autoplay=1&api=1`;
        }
    }
}
